// src/pages/ContactPage.js
import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./ContactPage.css";

const LOGO_ICON_SRC = "/logo-icon.png";
const LOGO_TEXT_SRC = "/logo-text-black.png";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function getApiBase() {
  const configured = safeString(process.env.REACT_APP_API_BASE_URL).trim();
  if (configured) return configured.replace(/\/+$/, "");

  if (window.location.hostname === "localhost") return "http://localhost:10000";

  return "https://api.flowtrak.net";
}

function isLikelyEmail(value) {
  const v = safeString(value).trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  storeName: "",
  roleTitle: "",
  monthlyRoCount: "",
  advisorCount: "",
  technicianCount: "",
  painPoint: "",
  message: "",
  website: "",
};

export default function ContactPage() {
  const { search } = useLocation();
  const guidedPilot = new URLSearchParams(search).get('interest') === 'guided-pilot';
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    message: guidedPilot ? 'I’m interested in the 30-Day Guided Pilot ($99 one-time) for one dealership rooftop.' : '',
  }));
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const canSubmit = useMemo(() => {
    return (
      safeString(form.name).trim() &&
      isLikelyEmail(form.email) &&
      safeString(form.storeName).trim() &&
      !sending
    );
  }, [form, sending]);

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submitInquiry(e) {
    e.preventDefault();
    setStatus("");
    setError("");

    const name = safeString(form.name).trim();
    const email = safeString(form.email).trim();
    const storeName = safeString(form.storeName).trim();

    if (!name || !email || !storeName) {
      setError("Please complete your name, email, and store name.");
      return;
    }

    if (!isLikelyEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setSending(true);

      const res = await fetch(`${getApiBase()}/api/contact/inquiry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({
          ...form,
          name,
          email,
          storeName,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Unable to send inquiry.");
      }

      setStatus(
        "Thank you — your inquiry was sent. We’ll review your store details and follow up with the best plan fit."
      );
      setForm(INITIAL_FORM);
    } catch (err) {
      setError(err?.message || "Unable to send inquiry.");
    } finally {
      setSending(false);
    }
  }

  return (
    <main className="ftc-page">
      <header className="ftc-nav">
        <Link to="/" className="ftc-brand" aria-label="Flowtrak home">
          <img
            src={LOGO_ICON_SRC}
            alt=""
            className="ftc-logoIcon"
            aria-hidden="true"
          />
          <img src={LOGO_TEXT_SRC} alt="Flowtrak" className="ftc-logoText" />
        </Link>

        <nav className="ftc-navLinks" aria-label="Contact page navigation">
          <Link to="/">Home</Link>
          <Link to="/login" className="ftc-loginLink">
            Login
          </Link>
        </nav>
      </header>

      <section className="ftc-shell" aria-labelledby="contact-page-title">
        <div className="ftc-copy">
          <div className="ftc-kicker">{guidedPilot ? "Guided pilot inquiry" : "Plan inquiry"}</div>

          <h1 id="contact-page-title">Tell us about your service department.</h1>

          <p>
            {guidedPilot ? "Apply for the 30-Day Guided Pilot: $99 one-time for one dealership rooftop. No long-term agreement. No automatic renewal. If you continue, the entire $99 is credited toward your first subscription month. We’ll review your workflow and follow up about fit and next steps." : "Flowtrak plans are matched to store volume and workflow needs. Send a few details and we’ll reply with the best plan and guidance on next steps."}
          </p>

          <div className="ftc-infoStack" aria-label="How the inquiry process works">
            <div className="ftc-infoCard">
              <span aria-hidden="true">1</span>
              <div>
                <strong>Share your store profile</strong>
                <p>Approximate RO volume, team size, and current workflow pain.</p>
              </div>
            </div>

            <div className="ftc-infoCard">
              <span aria-hidden="true">2</span>
              <div>
                <strong>We recommend a fit</strong>
                <p>{guidedPilot ? "We’ll discuss your workflow and readiness for personally guided onboarding." : "We’ll review whether Starter or High Volume makes sense."}</p>
              </div>
            </div>

            <div className="ftc-infoCard">
              <span aria-hidden="true">3</span>
              <div>
                <strong>You receive next steps</strong>
                <p>
                  {guidedPilot ? "We’ll confirm pilot availability and discuss setup and payment arrangements with you. Sending this inquiry does not take payment." : "We’ll send plan details, demo timing, and a secure Stripe link if appropriate."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <form
          className="ftc-form"
          onSubmit={submitInquiry}
          aria-labelledby="contact-form-title"
          aria-describedby="contact-form-note"
          noValidate
        >
          <div className="ftc-formTop">
            <div className="ftc-formEyebrow">Contact Flowtrak</div>
            <h2 id="contact-form-title">Store inquiry</h2>
            <p className="ftc-requiredNote">
              Required fields are marked with an asterisk.
            </p>
          </div>

          {error ? (
            <div className="ftc-alert ftc-alertError" role="alert">
              {error}
            </div>
          ) : null}

          {status ? (
            <div className="ftc-alert ftc-alertSuccess" role="status">
              {status}
            </div>
          ) : null}

          <div className="ftc-grid2">
            <div className="ftc-field">
              <label htmlFor="contact-name">
                Name <span aria-hidden="true">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                autoComplete="name"
                placeholder="Your name"
                required
                aria-required="true"
              />
            </div>

            <div className="ftc-field">
              <label htmlFor="contact-email">
                Email <span aria-hidden="true">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="name@dealer.com"
                required
                aria-required="true"
                aria-invalid={!!form.email && !isLikelyEmail(form.email)}
              />
            </div>

            <div className="ftc-field">
              <label htmlFor="contact-phone">Phone</label>
              <input
                id="contact-phone"
                name="phone"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                type="tel"
                autoComplete="tel"
                placeholder="Optional"
              />
            </div>

            <div className="ftc-field">
              <label htmlFor="contact-role-title">Job Role or Title</label>
              <input
                id="contact-role-title"
                name="roleTitle"
                value={form.roleTitle}
                onChange={(e) => updateField("roleTitle", e.target.value)}
                autoComplete="organization-title"
                placeholder="Service Manager, Fixed Ops, GM..."
              />
            </div>

            <div className="ftc-field">
              <label htmlFor="contact-store-name">
                Store Name <span aria-hidden="true">*</span>
              </label>
              <input
                id="contact-store-name"
                name="storeName"
                value={form.storeName}
                onChange={(e) => updateField("storeName", e.target.value)}
                autoComplete="organization"
                placeholder="Dealership or store name"
                required
                aria-required="true"
              />
            </div>

            <div className="ftc-field">
              <label htmlFor="contact-monthly-ro-count">
                Approx. Monthly RO Count
              </label>
              <select
                id="contact-monthly-ro-count"
                name="monthlyRoCount"
                value={form.monthlyRoCount}
                onChange={(e) => updateField("monthlyRoCount", e.target.value)}
              >
                <option value="">Select range</option>
                <option value="Under 250">Under 250</option>
                <option value="250-400">250–400</option>
                <option value="401-800">401–800</option>
                <option value="801-1500">801–1,500</option>
                <option value="1500+">1,500+</option>
              </select>
            </div>

            <div className="ftc-field">
              <label htmlFor="contact-pain-point">Main Pain Point</label>
              <select
                id="contact-pain-point"
                name="painPoint"
                value={form.painPoint}
                onChange={(e) => updateField("painPoint", e.target.value)}
              >
                <option value="">Select one</option>
                <option value="WIP visibility">WIP visibility</option>
                <option value="Dispatch control">Dispatch control</option>
                <option value="Parts delays">Parts delays</option>
                <option value="Advisor follow-up">Advisor follow-up</option>
                <option value="Promise time risk">Promise time risk</option>
                <option value="Reporting / accountability">
                  Reporting / accountability
                </option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="ftc-field">
              <label htmlFor="contact-advisor-count">Number of Advisors</label>
              <input
                id="contact-advisor-count"
                name="advisorCount"
                value={form.advisorCount}
                onChange={(e) => updateField("advisorCount", e.target.value)}
                inputMode="numeric"
                placeholder="Example: 8"
              />
            </div>

            <div className="ftc-field">
              <label htmlFor="contact-technician-count">
                Number of Technicians
              </label>
              <input
                id="contact-technician-count"
                name="technicianCount"
                value={form.technicianCount}
                onChange={(e) => updateField("technicianCount", e.target.value)}
                inputMode="numeric"
                placeholder="Example: 22"
              />
            </div>
          </div>

          <div className="ftc-field">
            <label htmlFor="contact-message">Message / Notes</label>
            <textarea
              id="contact-message"
              name="message"
              value={form.message}
              onChange={(e) => updateField("message", e.target.value)}
              rows={5}
              placeholder="Tell us what you want to improve first..."
            />
          </div>

          <label className="ftc-hp" aria-hidden="true">
            Website
            <input
              tabIndex="-1"
              autoComplete="off"
              value={form.website}
              onChange={(e) => updateField("website", e.target.value)}
            />
          </label>

          <button className="ftc-submit" type="submit" disabled={!canSubmit}>
            {sending ? "Sending…" : "Send inquiry"}
          </button>

          <div className="ftc-footnote" id="contact-form-note">
            Flowtrak will use this information to respond to your inquiry. This
            form does not create an account or start a subscription.
          </div>
        </form>
      </section>

      <footer className="ftc-footer">
        <div>© 2026 Flowtrak</div>

        <div className="ftc-footerLinks">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/dpa">DPA</Link>
          <Link to="/aup">AUP</Link>
          <Link to="/security">Security</Link>
          <Link to="/subprocessors">Subprocessors</Link>
          <Link to="/login">Login</Link>
        </div>
      </footer>
    </main>
  );
}
